import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { LibraryCard } from '@/features/routines/components/routine-library-section';
import { useRoutineLibraryFirst } from '@/features/routines/hooks/use-routine-library-first';
import { cn } from '@/lib/utils';

import type { BaseModalFCProps } from './modalsManager';

export const RoutineLibraryFirstModal = observer(
  function RoutineLibraryFirstModal({ open, onOpenChange }: BaseModalFCProps) {
    const flow = useRoutineLibraryFirst(() => onOpenChange(false));
    const disabled = flow.hasReachedLimit || flow.isSubmitting;

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content className="flex max-h-[82dvh] w-[calc(100vw-3rem)] max-w-[1080px] flex-col gap-0 overflow-hidden rounded-3xl p-0 sm:w-[calc(100vw-6rem)]">
          <Dialog.Header className="shrink-0 px-6 pb-5 pt-6 text-left md:px-8">
            <span className="text-sm text-foreground-muted">New routine</span>
            <Dialog.Title className="pr-8 font-serif text-2xl font-normal leading-tight md:text-3xl">
              What should Littlebird take off your plate?
            </Dialog.Title>
            <Dialog.Description className="pt-4 text-sm text-foreground-muted">
              Describe it in your own words — or pick one below
            </Dialog.Description>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                flow.submit();
              }}
            >
              <XStack className="mt-2 items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 shadow-sm focus-within:ring-2 focus-within:ring-foreground/20">
                <Icons.sparkles className="size-4 shrink-0 text-foreground-muted" />
                <textarea
                  aria-label="What would you like your routine to do?"
                  value={flow.prompt}
                  onChange={(event) => flow.setPrompt(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key !== 'Enter' ||
                      event.shiftKey ||
                      event.nativeEvent.isComposing ||
                      event.keyCode === 229
                    )
                      return;
                    event.preventDefault();
                    flow.submit();
                  }}
                  placeholder="e.g. Every Friday, summarize what my team shipped"
                  maxLength={4000}
                  rows={1}
                  disabled={disabled}
                  className="[field-sizing:content] max-h-32 min-h-6 min-w-0 flex-1 resize-none bg-transparent p-0 text-base leading-6 outline-none placeholder:text-foreground-muted"
                />
                <Button
                  type="submit"
                  aria-label="Continue in chat"
                  disabled={disabled || !flow.prompt.trim()}
                  className="size-11 shrink-0 rounded-full p-0"
                >
                  <Icons.arrowUp className="size-5" />
                </Button>
              </XStack>
            </form>
            {flow.hasReachedLimit && (
              <p role="status" className="pt-2 text-sm text-foreground-muted">
                You have reached your routine limit.
              </p>
            )}
          </Dialog.Header>

          <YStack className="min-h-0 flex-1 gap-5 overflow-y-auto px-6 pb-6 md:px-8">
            <XStack wrap className="gap-2" aria-label="Routine categories">
              {[{ slug: 'all', label: 'All' }, ...flow.tabs].map((tab) => (
                <button
                  key={tab.slug}
                  type="button"
                  aria-pressed={flow.activeTab === tab.slug}
                  onClick={() => flow.setActiveTab(tab.slug)}
                  className={cn(
                    'rounded-full border px-4 py-2 text-sm focus-ring',
                    flow.activeTab === tab.slug
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border bg-surface hover:bg-surface-hover'
                  )}
                >
                  {tab.slug === 'suggested' ? 'For you' : tab.label}
                </button>
              ))}
            </XStack>
            <XStack wrap className="items-center justify-between gap-3">
              <h2 className="text-sm font-medium">Library</h2>
              <input
                aria-label="Search routine library"
                placeholder="Search templates"
                value={flow.search}
                onChange={(event) => flow.setSearch(event.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-foreground/20 sm:w-64"
              />
            </XStack>
            {flow.library.isPending ? (
              <div
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                aria-label="Loading routine library"
              >
                {Array.from({ length: 8 }, (_, index) => (
                  <Skeleton key={index} className="h-52 rounded-2xl" />
                ))}
              </div>
            ) : flow.library.isError ? (
              <YStack className="items-center gap-3 py-8" role="alert">
                <p>Couldn’t load the routine library.</p>
                <Button
                  variant="secondary"
                  onClick={() => void flow.library.refetch()}
                >
                  Try again
                </Button>
              </YStack>
            ) : flow.items.length ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {flow.items.map((item) => (
                  <LibraryCard
                    key={item.id}
                    item={item}
                    onSelect={flow.selectTemplate}
                    disabled={disabled}
                    expanded
                  />
                ))}
              </div>
            ) : (
              <p
                role="status"
                className="py-8 text-center text-foreground-muted"
              >
                No templates match. Try another category or search.
              </p>
            )}
          </YStack>

          <Dialog.Footer className="!flex-row items-center !justify-between gap-3 border-t border-border px-6 py-4 md:px-8">
            <XStack wrap className="items-center gap-1 text-sm">
              <span className="text-foreground-muted">Prefer a form?</span>
              <Button
                variant="link"
                className="h-auto p-0 underline"
                onClick={flow.openManualSetup}
                disabled={disabled}
              >
                Set up manually
              </Button>
            </XStack>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    );
  }
);
